import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axiosConfig';

const FIELDS = [
  { value: 'email', label: 'Email', type: 'string' },
  { value: 'firstName', label: 'First Name', type: 'string' },
  { value: 'lastName', label: 'Last Name', type: 'string' },
  { value: 'tags', label: 'Tags', type: 'array' },
  { value: 'loyaltyPoints', label: 'Loyalty Points', type: 'number' },
  { value: 'createdAt', label: 'Customer Since', type: 'date' }
];

const OPERATORS = {
  string: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_null', label: 'is empty' },
    { value: 'is_not_null', label: 'is not empty' }
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'is greater than' },
    { value: 'less_than', label: 'is less than' },
    { value: 'greater_than_or_equal', label: 'is at least' },
    { value: 'less_than_or_equal', label: 'is at most' }
  ],
  array: [
    { value: 'has_tag', label: 'has tag' },
    { value: 'not_has_tag', label: 'does not have tag' }
  ],
  date: [
    { value: 'equals', label: 'is on' },
    { value: 'greater_than', label: 'is after' },
    { value: 'less_than', label: 'is before' }
  ]
};

const RuleRow = ({ rule, index, onUpdate, onRemove, existingTags, isDragging, onDragStart, onDragEnd, onDragOver }) => {
  const field = FIELDS.find(f => f.value === rule.field) || FIELDS[0];
  const operators = OPERATORS[field.type] || OPERATORS.string;
  const needsValue = !['is_null', 'is_not_null'].includes(rule.operator);

  const handleFieldChange = (newField) => {
    const newFieldType = FIELDS.find(f => f.value === newField)?.type || 'string';
    const defaultOperator = OPERATORS[newFieldType]?.[0]?.value || 'equals';
    onUpdate(index, { field: newField, operator: defaultOperator, value: '' });
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, index)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1rem',
        background: isDragging ? '#f3f4f6' : 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        marginBottom: '0.5rem',
        cursor: 'grab',
        transition: 'all 0.2s'
      }}
    >
      {/* Drag Handle */}
      <div style={{ color: '#9ca3af', cursor: 'grab', padding: '0 0.25rem' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="9" cy="5" r="2"/>
          <circle cx="15" cy="5" r="2"/>
          <circle cx="9" cy="12" r="2"/>
          <circle cx="15" cy="12" r="2"/>
          <circle cx="9" cy="19" r="2"/>
          <circle cx="15" cy="19" r="2"/>
        </svg>
      </div>

      {/* Field Select */}
      <select
        value={rule.field}
        onChange={(e) => handleFieldChange(e.target.value)}
        style={{
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          background: 'white',
          minWidth: '140px'
        }}
      >
        {FIELDS.map(f => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </select>

      {/* Operator Select */}
      <select
        value={rule.operator}
        onChange={(e) => onUpdate(index, { ...rule, operator: e.target.value })}
        style={{
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          background: 'white',
          minWidth: '150px'
        }}
      >
        {operators.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {/* Value Input */}
      {needsValue && (
        field.type === 'array' && existingTags.length > 0 ? (
          <select
            value={rule.value || ''}
            onChange={(e) => onUpdate(index, { ...rule, value: e.target.value })}
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: 'white'
            }}
          >
            <option value="">Select a tag...</option>
            {existingTags.map(tag => (
              <option key={tag.name} value={tag.name}>{tag.name}</option>
            ))}
          </select>
        ) : field.type === 'date' ? (
          <input
            type="date"
            value={rule.value || ''}
            onChange={(e) => onUpdate(index, { ...rule, value: e.target.value })}
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}
          />
        ) : field.type === 'number' ? (
          <input
            type="number"
            value={rule.value || ''}
            onChange={(e) => onUpdate(index, { ...rule, value: e.target.value })}
            placeholder="Enter value..."
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}
          />
        ) : (
          <input
            type="text"
            value={rule.value || ''}
            onChange={(e) => onUpdate(index, { ...rule, value: e.target.value })}
            placeholder="Enter value..."
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}
          />
        )
      )}

      {/* Remove Button */}
      <button
        onClick={() => onRemove(index)}
        style={{
          padding: '0.5rem',
          background: 'none',
          border: 'none',
          color: '#ef4444',
          cursor: 'pointer',
          borderRadius: '4px'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
  );
};

const VisualSegmentBuilder = ({ segment, existingTags, onSave, onCancel }) => {
  const [name, setName] = useState(segment?.name || '');
  const [description, setDescription] = useState(segment?.description || '');
  const [logic, setLogic] = useState(segment?.rules?.condition || 'AND');
  const [rules, setRules] = useState(
    segment?.rules?.rules || [{ field: 'tags', operator: 'has_tag', value: '' }]
  );
  const [preview, setPreview] = useState({ count: 0, customers: [] });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(null);

  // Fetch preview
  const fetchPreview = useCallback(async () => {
    if (rules.length === 0 || rules.some(r => !r.field || !r.operator)) {
      setPreview({ count: 0, customers: [] });
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await axiosInstance.post('/merchant/segments/test', {
        rules,
        logic
      });
      if (response.data.success) {
        setPreview({
          count: response.data.recipientCount,
          customers: response.data.preview || []
        });
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  }, [rules, logic]);

  // Debounced preview fetch
  useEffect(() => {
    const timer = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timer);
  }, [fetchPreview]);

  const addRule = () => {
    setRules([...rules, { field: 'email', operator: 'contains', value: '' }]);
  };

  const updateRule = (index, updatedRule) => {
    const newRules = [...rules];
    newRules[index] = updatedRule;
    setRules(newRules);
  };

  const removeRule = (index) => {
    if (rules.length === 1) return;
    setRules(rules.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
  };

  const handleDragOver = (e, targetIndex) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === targetIndex) return;

    const newRules = [...rules];
    const [draggedRule] = newRules.splice(draggingIndex, 1);
    newRules.splice(targetIndex, 0, draggedRule);
    setRules(newRules);
    setDraggingIndex(targetIndex);
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a segment name');
      return;
    }
    if (rules.length === 0) {
      alert('Please add at least one rule');
      return;
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      rules,
      logic
    });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '1.5rem' }}>
      {/* Builder Panel */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '1.5rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Segment name..."
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1.125rem',
              fontWeight: '600',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              marginBottom: '0.75rem'
            }}
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)..."
            style={{
              width: '100%',
              padding: '0.625rem 0.75rem',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
          />
        </div>

        {/* Logic Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: '#f9fafb',
          borderRadius: '6px'
        }}>
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>Match customers where</span>
          <div style={{
            display: 'flex',
            background: '#e5e7eb',
            borderRadius: '6px',
            padding: '0.125rem'
          }}>
            <button
              onClick={() => setLogic('AND')}
              style={{
                padding: '0.375rem 0.875rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: logic === 'AND' ? 'white' : '#6b7280',
                background: logic === 'AND' ? '#0d7463' : 'transparent',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              ALL rules match
            </button>
            <button
              onClick={() => setLogic('OR')}
              style={{
                padding: '0.375rem 0.875rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: logic === 'OR' ? 'white' : '#6b7280',
                background: logic === 'OR' ? '#0d7463' : 'transparent',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              ANY rule matches
            </button>
          </div>
        </div>

        {/* Rules */}
        <div style={{ marginBottom: '1rem' }}>
          {rules.map((rule, index) => (
            <RuleRow
              key={index}
              rule={rule}
              index={index}
              onUpdate={updateRule}
              onRemove={removeRule}
              existingTags={existingTags}
              isDragging={draggingIndex === index}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
            />
          ))}
        </div>

        {/* Add Rule Button */}
        <button
          onClick={addRule}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#0d7463',
            background: 'rgba(13, 116, 99, 0.06)',
            border: '1px dashed rgba(13, 116, 99, 0.22)',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>+</span>
          Add Rule
        </button>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.625rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            style={{
              padding: '0.625rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'white',
              background: name.trim() ? '#0d7463' : '#9ca3af',
              border: 'none',
              borderRadius: '6px',
              cursor: name.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            {segment ? 'Update Segment' : 'Create Segment'}
          </button>
        </div>
      </div>

      {/* Preview Panel */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '1.25rem',
        height: 'fit-content',
        position: 'sticky',
        top: '1rem'
      }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem' }}>
          Live Preview
        </h3>

        {/* Count */}
        <div style={{
          padding: '1rem',
          background: loadingPreview ? '#f9fafb' : 'rgba(13, 116, 99, 0.06)',
          borderRadius: '8px',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {loadingPreview ? (
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Calculating...</div>
          ) : (
            <>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0d7463' }}>
                {preview.count}
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#065f46' }}>
                {preview.count === 1 ? 'customer matches' : 'customers match'}
              </div>
            </>
          )}
        </div>

        {/* Customer List */}
        <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.5rem' }}>
          Preview (first 20)
        </div>
        <div style={{
          maxHeight: '300px',
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '6px'
        }}>
          {preview.customers.length === 0 ? (
            <div style={{
              padding: '1.5rem',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: '0.8125rem'
            }}>
              {loadingPreview ? 'Loading...' : 'No customers match'}
            </div>
          ) : (
            preview.customers.map((customer, idx) => (
              <div
                key={customer.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 0.75rem',
                  borderBottom: idx < preview.customers.length - 1 ? '1px solid #f3f4f6' : 'none'
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '999px',
                  background: '#e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '0.625rem',
                  fontSize: '0.6875rem',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  {(customer.firstName?.[0] || customer.email?.[0] || '?').toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{
                    fontSize: '0.8125rem',
                    fontWeight: '500',
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {customer.firstName || customer.username || 'Customer'} {customer.lastName || ''}
                  </div>
                  <div style={{
                    fontSize: '0.6875rem',
                    color: '#9ca3af',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {customer.email}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Segment Summary */}
        {rules.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#f9fafb',
            borderRadius: '6px',
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            <div style={{ marginBottom: '0.25rem', fontWeight: '500' }}>Segment Rules:</div>
            <div style={{ lineHeight: 1.6 }}>
              {rules.map((rule, idx) => {
                const fieldLabel = FIELDS.find(f => f.value === rule.field)?.label || rule.field;
                const field = FIELDS.find(f => f.value === rule.field);
                const operatorLabel = OPERATORS[field?.type]?.find(o => o.value === rule.operator)?.label || rule.operator;

                return (
                  <span key={idx}>
                    {idx > 0 && <span style={{ fontWeight: '600', color: '#0d7463' }}> {logic} </span>}
                    <span>{fieldLabel}</span>
                    <span style={{ fontStyle: 'italic' }}> {operatorLabel}</span>
                    {rule.value && <span style={{ fontWeight: '500' }}> "{rule.value}"</span>}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualSegmentBuilder;
